/**
 * Revision Service
 *
 * Handles Revision Studio operations: fetching samples, managing revisions,
 * and comparing before/after analysis results.
 */

import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import type {
  RevisionSample,
  StoryRevision,
  StoryRevisionInsert,
  ChallengeSubmission,
  ChallengeDifficulty,
  AgeRange,
} from '@/types/database'
import { analyzeStructure, compareAnalyses, calculateStoryScore } from './structureAnalyzer'
import { type StructureAnalysisResult, ANALYZER_VERSION } from '@/types/domain'

// =============================================================================
// Revision Sample Queries
// =============================================================================

export interface SampleFilters {
  difficulty?: ChallengeDifficulty
  ageRange?: AgeRange
  focusArea?: string
}

/**
 * Get all published revision samples, optionally filtered
 */
export async function getRevisionSamples(
  filters?: SampleFilters
): Promise<RevisionSample[]> {
  if (!isSupabaseConfigured()) return []

  let query = supabase
    .from('revision_samples')
    .select('*')
    .not('published_at', 'is', null)
    .is('deleted_at', null)
    .order('difficulty', { ascending: true })
    .order('created_at', { ascending: false })

  if (filters?.difficulty) {
    query = query.eq('difficulty', filters.difficulty)
  }

  if (filters?.ageRange) {
    query = query.contains('age_ranges', [filters.ageRange])
  }

  if (filters?.focusArea) {
    query = query.contains('focus_areas', [filters.focusArea])
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching revision samples:', error)
    throw error
  }

  return (data as RevisionSample[]) || []
}

/**
 * Get a single revision sample by ID
 */
export async function getRevisionSample(
  sampleId: string
): Promise<RevisionSample | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('revision_samples')
    .select('*')
    .eq('id', sampleId)
    .not('published_at', 'is', null)
    .is('deleted_at', null)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // Not found
    console.error('Error fetching revision sample:', error)
    throw error
  }

  return data as RevisionSample
}

/**
 * Get unique focus areas from all samples (for filters)
 */
export async function getSampleFocusAreas(): Promise<string[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('revision_samples')
    .select('focus_areas')
    .not('published_at', 'is', null)
    .is('deleted_at', null)

  if (error) {
    console.error('Error fetching focus areas:', error)
    return []
  }

  const allAreas = new Set<string>()
  for (const sample of data as { focus_areas: string[] }[]) {
    sample.focus_areas?.forEach(area => allAreas.add(area))
  }
  return Array.from(allAreas).sort()
}

// =============================================================================
// Story Revision Queries
// =============================================================================

/**
 * Get all revisions for a child
 */
export async function getRevisionsForChild(
  childId: string
): Promise<StoryRevision[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('story_revisions')
    .select('*')
    .eq('child_id', childId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching revisions:', error)
    throw error
  }

  return (data as StoryRevision[]) || []
}

/**
 * Get a single revision by ID
 */
export async function getRevision(
  revisionId: string
): Promise<StoryRevision | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('story_revisions')
    .select('*')
    .eq('id', revisionId)
    .is('deleted_at', null)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    console.error('Error fetching revision:', error)
    throw error
  }

  return data as StoryRevision
}

/**
 * Get revision with its source data (sample or submission)
 */
export async function getRevisionWithSource(
  revisionId: string
): Promise<{
  revision: StoryRevision
  sample?: RevisionSample
  submission?: ChallengeSubmission
} | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('story_revisions')
    .select(`
      *,
      revision_samples (*),
      challenge_submissions (*)
    `)
    .eq('id', revisionId)
    .is('deleted_at', null)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    console.error('Error fetching revision with source:', error)
    throw error
  }

  const revisionData = data as StoryRevision & {
    revision_samples: RevisionSample | null
    challenge_submissions: ChallengeSubmission | null
  }

  return {
    revision: {
      ...revisionData,
      revision_samples: undefined,
      challenge_submissions: undefined,
    } as StoryRevision,
    sample: revisionData.revision_samples || undefined,
    submission: revisionData.challenge_submissions || undefined,
  }
}

/**
 * Check if a child has an in-progress revision for a sample
 */
export async function getInProgressRevisionForSample(
  childId: string,
  sampleId: string
): Promise<StoryRevision | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('story_revisions')
    .select('*')
    .eq('child_id', childId)
    .eq('sample_id', sampleId)
    .eq('status', 'in_progress')
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(1)

  if (error) {
    console.error('Error checking in-progress revision:', error)
    return null
  }

  return (data?.[0] as StoryRevision) || null
}

/**
 * Check if a child has an in-progress revision for a submission
 */
export async function getInProgressRevisionForSubmission(
  childId: string,
  submissionId: string
): Promise<StoryRevision | null> {
  if (!isSupabaseConfigured()) return null

  const { data, error } = await supabase
    .from('story_revisions')
    .select('*')
    .eq('child_id', childId)
    .eq('submission_id', submissionId)
    .eq('status', 'in_progress')
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(1)

  if (error) {
    console.error('Error checking in-progress revision:', error)
    return null
  }

  return (data?.[0] as StoryRevision) || null
}

// =============================================================================
// Revision Mutations
// =============================================================================

/**
 * Create a new revision from a sample
 */
export async function createRevisionFromSample(
  childId: string,
  sampleId: string
): Promise<StoryRevision> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  // Get the sample
  const sample = await getRevisionSample(sampleId)
  if (!sample) {
    throw new Error('Sample not found')
  }

  // Analyze the original content
  const originalAnalysis = analyzeStructure(sample.original_content)

  const insert: StoryRevisionInsert = {
    child_id: childId,
    source_type: 'sample',
    sample_id: sampleId,
    original_content: sample.original_content,
    revised_content: sample.original_content, // Start with original
    analysis_version: ANALYZER_VERSION,
    original_analysis: originalAnalysis as unknown as StoryRevisionInsert['original_analysis'],
    status: 'in_progress',
  }

  const { data, error } = await supabase
    .from('story_revisions')
    .insert(insert as Record<string, unknown>)
    .select()
    .single()

  if (error) {
    console.error('Error creating revision:', error)
    throw error
  }

  return data as StoryRevision
}

/**
 * Create a new revision from a challenge submission
 */
export async function createRevisionFromSubmission(
  childId: string,
  submissionId: string
): Promise<StoryRevision> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  // Get the submission
  const { data: submission, error: fetchError } = await supabase
    .from('challenge_submissions')
    .select('*')
    .eq('id', submissionId)
    .is('deleted_at', null)
    .single()

  if (fetchError || !submission) {
    throw new Error('Submission not found')
  }

  const submissionData = submission as ChallengeSubmission

  // Use existing analysis or create new
  const originalAnalysis = submissionData.analysis_result
    ? (submissionData.analysis_result as unknown as StructureAnalysisResult)
    : analyzeStructure(submissionData.content)

  const insert: StoryRevisionInsert = {
    child_id: childId,
    source_type: 'submission',
    submission_id: submissionId,
    original_content: submissionData.content,
    revised_content: submissionData.content, // Start with original
    analysis_version: ANALYZER_VERSION,
    original_analysis: originalAnalysis as unknown as StoryRevisionInsert['original_analysis'],
    status: 'in_progress',
  }

  const { data, error } = await supabase
    .from('story_revisions')
    .insert(insert as Record<string, unknown>)
    .select()
    .single()

  if (error) {
    console.error('Error creating revision:', error)
    throw error
  }

  return data as StoryRevision
}

/**
 * Update revision content (auto-save while editing)
 */
export async function updateRevisionContent(
  revisionId: string,
  revisedContent: string
): Promise<StoryRevision> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  const { data, error } = await supabase
    .from('story_revisions')
    .update({
      revised_content: revisedContent,
      updated_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', revisionId)
    .select()
    .single()

  if (error) {
    console.error('Error updating revision:', error)
    throw error
  }

  return data as StoryRevision
}

/**
 * Complete a revision - analyze revised content and calculate improvement
 */
export async function completeRevision(
  revisionId: string
): Promise<{
  revision: StoryRevision
  originalAnalysis: StructureAnalysisResult
  revisedAnalysis: StructureAnalysisResult
  improvement: ReturnType<typeof compareAnalyses>
}> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  // Get current revision
  const revision = await getRevision(revisionId)
  if (!revision) {
    throw new Error('Revision not found')
  }

  // Analyze revised content
  const revisedAnalysis = analyzeStructure(revision.revised_content)

  // Get or recreate original analysis
  const originalAnalysis = revision.original_analysis
    ? (revision.original_analysis as unknown as StructureAnalysisResult)
    : analyzeStructure(revision.original_content)

  // Calculate improvement
  const improvement = compareAnalyses(originalAnalysis, revisedAnalysis)

  // Calculate improvement score (difference in overall scores)
  const originalScore = calculateStoryScore(originalAnalysis)
  const revisedScore = calculateStoryScore(revisedAnalysis)
  const improvementScore = revisedScore - originalScore

  // Update revision with analysis results
  const { data, error } = await supabase
    .from('story_revisions')
    .update({
      status: 'completed',
      completed_at: new Date().toISOString(),
      analysis_version: ANALYZER_VERSION,
      original_analysis: originalAnalysis,
      revised_analysis: revisedAnalysis,
      improvement_score: improvementScore,
      updated_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', revisionId)
    .select()
    .single()

  if (error) {
    console.error('Error completing revision:', error)
    throw error
  }

  return {
    revision: data as StoryRevision,
    originalAnalysis,
    revisedAnalysis,
    improvement,
  }
}

/**
 * Revert revision content to original
 */
export async function revertToOriginal(
  revisionId: string
): Promise<StoryRevision> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  // Get current revision
  const revision = await getRevision(revisionId)
  if (!revision) {
    throw new Error('Revision not found')
  }

  const { data, error } = await supabase
    .from('story_revisions')
    .update({
      revised_content: revision.original_content,
      updated_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', revisionId)
    .select()
    .single()

  if (error) {
    console.error('Error reverting revision:', error)
    throw error
  }

  return data as StoryRevision
}

/**
 * Delete a revision (soft delete)
 */
export async function deleteRevision(revisionId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured')
  }

  const { error } = await supabase
    .from('story_revisions')
    .update({
      deleted_at: new Date().toISOString(),
    } as Record<string, unknown>)
    .eq('id', revisionId)

  if (error) {
    console.error('Error deleting revision:', error)
    throw error
  }
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get revision statistics for a child
 */
export async function getRevisionStats(childId: string): Promise<{
  total: number
  completed: number
  inProgress: number
  averageImprovement: number
}> {
  if (!isSupabaseConfigured()) {
    return { total: 0, completed: 0, inProgress: 0, averageImprovement: 0 }
  }

  const { data, error } = await supabase
    .from('story_revisions')
    .select('status, improvement_score')
    .eq('child_id', childId)
    .is('deleted_at', null)

  if (error) {
    console.error('Error fetching revision stats:', error)
    return { total: 0, completed: 0, inProgress: 0, averageImprovement: 0 }
  }

  const revisions = data as { status: string; improvement_score: number | null }[]

  const total = revisions.length
  const completed = revisions.filter(r => r.status === 'completed').length
  const inProgress = revisions.filter(r => r.status === 'in_progress').length

  const improvements = revisions
    .filter(r => r.improvement_score !== null)
    .map(r => r.improvement_score as number)

  const averageImprovement =
    improvements.length > 0
      ? improvements.reduce((a, b) => a + b, 0) / improvements.length
      : 0

  return {
    total,
    completed,
    inProgress,
    averageImprovement: Math.round(averageImprovement),
  }
}

/**
 * Get completed revisions with improvement data
 */
export async function getCompletedRevisions(
  childId: string,
  limit: number = 10
): Promise<StoryRevision[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('story_revisions')
    .select('*')
    .eq('child_id', childId)
    .eq('status', 'completed')
    .is('deleted_at', null)
    .order('completed_at', { ascending: false })
    .limit(limit)

  if (error) {
    console.error('Error fetching completed revisions:', error)
    throw error
  }

  return (data as StoryRevision[]) || []
}
